import * as THREE from 'three';

// Render a procedural reticle on a quad, most likely a screenspace
export default class ReticleMaterial extends THREE.RawShaderMaterial {
  constructor(props?: {
    // Reticle
    useBox?: boolean;
    hMin?: number;
    hMax?: number;
    hPad?: number;
    vMin?: number;
    vMax?: number;
    vPad?: number;
    rColor?: THREE.Color;
    // Border
    padding?: number;
    radius?: number;
    bColor?: THREE.Color;

    depth?: boolean;
  }) {
    super();

    // Merge custom props with default options
    const opts = Object.assign(
      {
        // Reticle
        useBox: true,
        hMin: 0.3,
        hMax: 0.9,
        hPad: 0.1,
        vMin: 0.3,
        vMax: 0.9,
        vPad: 0.1,
        rColor: new THREE.Color('#ffffff'),
        // Border
        padding: 10,
        radius: 30,
        bColor: new THREE.Color('#c0c0c0'),

        depth: false,
      },
      props,
    );

    this.name = 'ReticleMaterial';
    this.glslVersion = THREE.GLSL3;
    this.depthTest = opts.depth;
    this.transparent = true;

    this.uniforms = {
      screenWidth: { value: 0 },
      screenHeight: { value: 0 },

      // Reticle
      useBox: { value: opts.useBox ? 1 : 0 },
      hMin: { value: opts.hMin },
      hMax: { value: opts.hMax },
      hPad: { value: opts.hPad },
      vMin: { value: opts.vMin },
      vMax: { value: opts.vMax },
      vPad: { value: opts.vPad },
      rColor: { value: opts.rColor },

      // Border
      padding: { value: opts.padding },
      radius: { value: opts.radius },
      bColor: { value: opts.bColor },
    };

    this.vertexShader = `
        in vec3 position;
        in vec3 normal;
        in vec2 uv;

        uniform mat4 modelMatrix;
        uniform mat4 viewMatrix;
        uniform mat4 projectionMatrix;

        out vec2 fragUV;

        // #####################################################################

        void main(){
            fragUV      = uv;
            gl_Position = vec4( position.xy, 0.0, 1.0 );;
        }`;

    this.fragmentShader = `precision mediump float;
        in vec2  fragUV;
        out vec4 outColor;

        uniform float screenWidth;
        uniform float screenHeight;

        uniform float useBox;
        uniform float hMin;
        uniform float hMax;
        uniform float hPad;
        uniform float vMin;
        uniform float vMax;
        uniform float vPad;
        uniform vec3 rColor;

        uniform float padding;
        uniform float radius;
        uniform vec3 bColor;

        // #####################################################################

        float screenReticle( vec2 res ){
            // Compute various spaces of UV
            vec2 cuv  = ( fragUV - vec2( 0.5 ) ) * 2.0;   // Center UV & Remap back to -1:1
            vec2 acuv = abs( cuv );
            vec2 suv  = cuv * vec2( res.x / res.y, 1.0 ); // Apply screen ratio
            vec2 asuv = abs( suv );

            vec2 fw   = fwidth( asuv ); // Anti-aliasing

            // Full Bar Masks using screen fixed UV
            float hMask = 1.0 - smoothstep( hPad-fw.x, hPad+fw.x, asuv.y );
            float vMask = 1.0 - smoothstep( vPad-fw.y, vPad+fw.y, asuv.x );

            // Clip Bars with min & max length limits
            float ux     = useBox > 0.5? asuv.x : acuv.x;       // Use fixed or unfixed UV for clip control
            float hClip  = step( hMin, ux ) * step( ux, hMax );            
            float vClip  = step( vMin, acuv.y ) * step( acuv.y, vMax );

            // Final Mask
            return max( vMask * vClip, hMask * hClip );
        }

        float innerRounding( vec2 px, vec2 res, float borderWidth, float radius ){
            // Calculate position relative to rectangle edges
            vec2 halfRes        = res * 0.5;
            vec2 distFromCenter = abs( px - halfRes );
            
            // Calculate inner rounded rectangle
            vec2 innerCorner    = halfRes - vec2( radius + borderWidth );
            vec2 innerDist      = max( distFromCenter - innerCorner, 0.0 );
            float mask          = max( 0.0, min( 1.0, length( innerDist ) - radius )); // Clamp

            return mask;
        }

        // #####################################################################

        void main(){
            // Setup Data
            vec2 res    = vec2( screenWidth, screenHeight );
            vec2 px     = res * fragUV; // gl_FragCoord.xy; can't use gl_FragCoord in mini view as its actual canvas pixel coord

            // Calc Masks
            float rMask = screenReticle( res );
            float bMask = innerRounding( px, res, padding, radius );

            // Colorize + Mix Masks
            vec3 color = mix( rMask * rColor, bMask * bColor, step( 0.999, bMask ) );            
            outColor   = vec4( color, max( bMask, rMask ) );
        }`;
  }

  setScreenSize(w: number, h: number): this {
    this.uniforms.screenWidth.value = w;
    this.uniforms.screenHeight.value = h;
    return this;
  }
}
